import type {PaymentCategory} from '../../models'
import type {
  BillingAmountSource,
  BillingPeriodType,
  BillingPricingOption,
  BillingRef,
  ChargeResolutionStatus,
  StudentCharge,
  StudentChargeStatus,
  StudentFeeAgreement,
} from './types'

export {
  chargeDisplayedPaidAmount,
  chargePaymentValidation,
  type ChargePaidDisplaySource,
  type ChargePaymentValidation,
} from './chargePaidAmount'

export function billingRefLabel(ref: BillingRef | undefined): string {
  if (!ref) return '—'
  if (typeof ref === 'string') return ref
  if (ref.name) return ref.name
  const parts = [ref.firstName, ref.lastName].filter(Boolean)
  return parts.length ? parts.join(' ') : ref._id
}

export function billingRefId(ref: BillingRef | undefined): string | undefined {
  if (!ref) return undefined
  return typeof ref === 'string' ? ref : ref._id
}

/** Staff may waive or cancel charges in these statuses. */
export function isChargeCancellable(charge: StudentCharge): boolean {
  return charge.status === 'open' || charge.status === 'partial' || charge.status === 'overdue'
}

/** Permanent delete — only inactive charges with no payments (see BILLING-API.md). */
export function isChargeDeletable(charge: StudentCharge): boolean {
  if (!charge._id) return false
  if (charge.status !== 'cancelled' && charge.status !== 'waived') return false
  return (charge.amountPaid ?? 0) === 0
}

export function isChargeInactive(charge: StudentCharge): boolean {
  return charge.status === 'cancelled' || charge.status === 'waived'
}

/** Past due with balance remaining (registry “overdue only” filter). */
export function isChargePastDue(charge: StudentCharge, now = Date.now()): boolean {
  if (charge.status === 'overdue') return true
  if (charge.status !== 'open' && charge.status !== 'partial') return false
  if (!charge.dueDate) return false
  const due = Number(charge.amountDue) || 0
  const paid = Number(charge.amountPaid) || 0
  const remaining =
    charge.amountRemaining != null ? Number(charge.amountRemaining) : Math.max(due - paid, 0)
  return new Date(charge.dueDate).getTime() < now && remaining > 0
}

export function chargeStatusBadgeVariant(
  status: StudentChargeStatus | undefined
): 'success' | 'warning' | 'danger' | 'secondary' | 'info' {
  switch (status) {
    case 'paid':
      return 'success'
    case 'partial':
      return 'info'
    case 'overdue':
      return 'danger'
    case 'waived':
    case 'cancelled':
      return 'secondary'
    default:
      return 'warning'
  }
}

export function formatBillingMoney(amount: number | undefined, currency = 'TND'): string {
  if (amount == null || Number.isNaN(amount)) return '—'
  return `${amount.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 3})} ${currency}`
}

export function formatPricingOptionLabel(option: BillingPricingOption): string {
  const name = option.categoryName ?? option.paymentCategoryId
  const resolved = option.resolvedAmount ?? option.suggestedAmount ?? option.predefinedAmount
  const suggested = option.suggestedAmount ?? option.predefinedAmount
  const cur = option.currency ?? 'TND'
  if (resolved != null && suggested != null && resolved !== suggested) {
    return `${name} — ${formatBillingMoney(resolved, cur)} (${formatBillingMoney(suggested, cur)} class)`
  }
  if (resolved != null) return `${name} — ${formatBillingMoney(resolved, cur)}`
  return name
}

export function resolutionStatusBadgeVariant(
  status: ChargeResolutionStatus | undefined
): 'success' | 'warning' | 'danger' | 'secondary' | 'info' {
  switch (status) {
    case 'resolved':
      return 'success'
    case 'fallback_class':
      return 'info'
    case 'custom':
      return 'secondary'
    case 'missing_rule_manual_review':
      return 'danger'
    default:
      return 'secondary'
  }
}

export function chargeResolutionStatusKey(status: ChargeResolutionStatus | undefined): string {
  if (!status) return 'BILLING.RESOLUTION.UNKNOWN'
  return `BILLING.RESOLUTION.${status.toUpperCase()}`
}

export function amountSourceKey(source: BillingAmountSource | undefined): string {
  if (!source) return 'BILLING.AMOUNT_SOURCE.UNKNOWN'
  return `BILLING.AMOUNT_SOURCE.${source.toUpperCase()}`
}

export function billingPeriodTypeKey(type: BillingPeriodType | undefined): string {
  if (!type) return 'BILLING.PERIOD_TYPE.UNKNOWN'
  return `BILLING.PERIOD_TYPE.${type.toUpperCase()}`
}

export function billingPeriodTypeBadgeVariant(
  type: BillingPeriodType | undefined
): 'primary' | 'secondary' | 'info' {
  if (type === 'custom_schedule') return 'info'
  if (type === 'policy_period') return 'secondary'
  return 'secondary'
}

export function formatChargePeriodLabel(charge: StudentCharge): string {
  return charge.billingPeriod ?? '—'
}

export function chargeScheduleName(charge: StudentCharge): string | undefined {
  return charge.billingScheduleSnapshot?.scheduleName
}

/** Human label for agreement scope (full year vs one billing period). */
export function formatFeeAgreementScope(
  agreement: StudentFeeAgreement,
  formatMessage: (descriptor: {id: string; defaultMessage: string}, values?: Record<string, string>) => string
): string {
  if (agreement.billingPeriod?.trim()) {
    return agreement.billingPeriod.trim()
  }
  if (agreement.academicYear) {
    return formatMessage(
      {
        id: 'BILLING.AGREEMENT_SCOPE_YEAR',
        defaultMessage: 'Full year {year}',
      },
      {year: agreement.academicYear}
    )
  }
  return formatMessage({
    id: 'BILLING.AGREEMENT_SCOPE_ALL',
    defaultMessage: 'Full school year',
  })
}

export function feeAgreementCategoryLabel(
  agreement: StudentFeeAgreement,
  paymentCategories: PaymentCategory[] = []
): string {
  if (agreement.categoryName) return agreement.categoryName
  if (agreement.paymentCategory) {
    const label = billingRefLabel(agreement.paymentCategory)
    if (label !== '—') return label
  }
  const rawId = agreement.paymentCategoryId ?? billingRefId(agreement.paymentCategory)
  if (!rawId) return '—'
  const id = String(rawId)
  const match = paymentCategories.find((c) => String(c._id) === id)
  return match?.name ?? id
}
